<template>
  <div class="page-content">
    <div class="script-config">
      <div class="page-header">
        <div class="header-info">
          <div class="config-count-info">
            <span v-if="targetUserId" class="view-user-info" @click="handleBackUserList">
              {{ t('script.viewUser', { user: targetUsername || targetUserId }) }}
            </span>
            <span class="count-text">{{ t('script.configCount', { count: configList.length }) }}</span>
            <span class="count-tip">{{ t('script.configLimitTip') }}</span>
          </div>
        </div>
        <div class="header-actions">
          <div
            v-if="!isViewMode"
            class="custom-upload-btn"
            :class="{ disabled: configList.length >= 100 }"
            @click="showAddConfigDialog"
          >
            <i class="el-icon-plus"></i>
            <span>{{ t('script.addConfig') }}</span>
          </div>
        </div>
      </div>

      <!-- 配置列表 -->
      <div class="config-list-container">
        <div v-if="configLoading" class="loading-container">
          <div class="loading-spinner">
            <div class="spinner"></div>
            <p>{{ t('common.loading') }}</p>
          </div>
        </div>
        <div v-else class="table-container">
          <div class="table-header">
            <div class="header-cell name-col">{{ t('script.colName') }}</div>
            <div class="header-cell game-col">{{ t('script.colGame') }}</div>
            <div class="header-cell description-col">{{ t('script.colDesc') }}</div>
            <div v-if="isConfigAdmin && !isViewMode" class="header-cell shared-col">{{ t('script.colShared') }}</div>
            <div class="header-cell device-count-col">{{ t('script.colDevices') }}</div>
            <div class="header-cell time-col">{{ t('script.colCreated') }}</div>
            <div class="header-cell actions-col">{{ t('script.colActions') }}</div>
          </div>
          <div class="table-body">
            <div v-if="configList.length === 0" class="empty-state">
              <div class="empty-icon">📋</div>
              <div class="empty-text">{{ t('script.empty') }}</div>
              <div class="empty-desc">{{ t('script.emptyHint') }}</div>
            </div>
            <div v-else v-for="config in configList" :key="config.id" class="table-row">
              <div class="cell name-col">{{ config.name }}</div>
              <div class="cell game-col">{{ config.game }}</div>
              <div class="cell description-col">{{ config.description || '-' }}</div>
              <div v-if="isConfigAdmin && !isViewMode" class="cell shared-col">
                <el-switch
                  v-model="config.isShared"
                  :active-value="1"
                  :inactive-value="0"
                  :active-text="t('script.shared')"
                  :inactive-text="t('script.private')"
                  @change="handleShareChange(config)"
                />
              </div>
              <div class="cell device-count-col">{{ config.deviceCount || 0 }}</div>
              <div class="cell time-col">{{ formatDate(config.createTime) }}</div>
              <div class="cell actions-col">
                <div class="config-actions">
                  <!-- 管理员查看模式下，隐藏复制配置按钮 -->
                  <button v-if="!isViewMode" class="action-btn copy" @click="copyConfigHandler(config)">
                    {{ t('script.copy') }}
                  </button>
                  <!-- 管理员查看模式下，显示"查看"按钮，否则显示"编辑"按钮 -->
                  <button class="action-btn edit" @click="editConfig(config)">
                    {{ isViewMode ? t('script.view') : t('script.edit') }}
                  </button>
                  <!-- 管理员查看模式下，隐藏删除按钮和共享配置标识 -->
                  <template v-if="!isViewMode">
                    <!-- 只有自己的配置才能删除 -->
                    <template v-if="isOwnConfig(config)">
                      <button
                        v-if="!isDefaultConfig(config)"
                        class="action-btn delete"
                        @click="deleteConfigConfirm(config)"
                      >
                        {{ t('script.delete') }}
                      </button>
                      <span
                        v-else
                        class="default-config-tag"
                        :title="t('script.defaultConfigTip')"
                      >
                        {{ t('script.defaultConfig') }}
                      </span>
                    </template>
                    <!-- 共享配置显示标识 -->
                    <span
                      v-else
                      class="shared-config-tag"
                      title="这是其他用户共享的配置，只能查看不能修改"
                    >
                      共享配置
                    </span>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage, ElMessageBox, ElSwitch } from 'element-plus'
import {
  getUserConfigs,
  deleteConfig,
  copyConfig,
  setConfigShared
} from '../api/configs'
import { t } from '@/locales/zh'

export default {
  name: 'ScriptConfig',
  components: {
    ElSwitch
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const configLoading = ref(false)
    const configList = ref([])

    // 状态筛选相关
    const selectedUserStatus = ref('')
    
    // 查看其他用户的配置（管理员功能）
    const targetUserId = computed(() => {
      const userId = route.query.userId
      return userId ? parseInt(userId) : null
    })
    
    const targetUsername = computed(() => {
      return route.query.username || null
    })
    
    // 判断是否是管理员查看模式（只读模式）
    const isViewMode = computed(() => {
      return !!targetUserId.value
    })

    // 检查是否是配置管理员
    const isConfigAdmin = computed(() => {
      try {
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
        return userInfo.is_admin === 3 || userInfo.is_admin === 1
      } catch (error) {
        return false
      }
    })

    // 获取当前用户的游戏类型
    const currentUserGame = computed(() => {
      try {
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
        return userInfo.game || '冒险岛枫星'
      } catch (error) {
        console.error('获取用户游戏类型失败:', error)
        return '冒险岛枫星'
      }
    })

    // 加载配置数据（支持状态筛选和查看其他用户的配置）
    const loadConfigs = async () => {
      configLoading.value = true
      try {
        // 传递游戏类型和用户状态参数，后端直接过滤
        const userStatus = selectedUserStatus.value || null
        // 如果指定了targetUserId，则查看该用户的配置（管理员功能）
        const userId = targetUserId.value
        const game = userId ? null : currentUserGame.value // 查看其他用户配置时，不按游戏过滤
        const configsResponse = await getUserConfigs(game, userStatus, userId)
        configList.value = configsResponse.data || []
        if (userId) {
          console.log('加载用户配置成功，用户ID:', userId, '配置数量:', configList.value.length)
        } else {
          console.log('加载配置成功，用户游戏类型:', currentUserGame.value, '用户状态:', userStatus, '配置数量:', configList.value.length)
        }
      } catch (error) {
        console.error('加载配置失败:', error)
        const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          '未知错误'
        ElMessage.error('加载配置失败: ' + errorMessage)
      } finally {
        configLoading.value = false
      }
    }

    // 处理用户状态筛选变化
    const handleUserStatusChange = () => {
      loadConfigs()
    }

    // 设置配置共享状态
    const handleShareChange = async (config) => {
      try {
        await setConfigShared(config.id, config.isShared)
        ElMessage.success('设置共享状态成功')
      } catch (error) {
        console.error('设置共享状态失败:', error)
        const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          '未知错误'
        ElMessage.error('设置共享状态失败: ' + errorMessage)
        // 恢复原状态
        config.isShared = config.isShared === 1 ? 0 : 1
      }
    }

    // 跳转到配置创建页面
    const showAddConfigDialog = () => {
      if (configList.value.length >= 100) {
        ElMessage.warning('每个用户最多只能创建100个配置')
        return
      }
      router.push('/dashboard/scripts/create')
    }

    // 编辑/查看配置
    const editConfig = (config) => {
      // 如果是查看模式，添加viewMode参数
      if (isViewMode.value) {
        router.push({
          path: `/dashboard/scripts/edit/${config.id}`,
          query: { viewMode: 'true' }
        })
      } else {
        router.push(`/dashboard/scripts/edit/${config.id}`)
      }
    }

    // 删除配置确认
    const deleteConfigConfirm = async (config) => {
      try {
        await ElMessageBox.confirm(
          `确定要删除配置"${config.name}"吗？删除后无法恢复。`,
          '删除确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          }
        )
        
        await deleteConfig(config.id)
        ElMessage.success('配置删除成功')
        loadConfigs() // 重新加载配置列表
      } catch (error) {
        if (error === 'cancel') return
        console.error('删除配置失败:', error)
        const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          '未知错误'
        ElMessage.error('删除配置失败: ' + errorMessage)
      }
    }

    // 复制配置
    const copyConfigHandler = async (config) => {
      try {
        // 检查配置数量限制
        if (configList.value.length >= 100) {
          ElMessage.warning('每个用户最多只能创建100个配置')
          return
        }

        await ElMessageBox.confirm(
          `确定要复制配置"${config.name}"吗？`,
          '复制确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'info',
          }
        )
        
        // 调用后端复制配置API
        await copyConfig(config.id)
        ElMessage.success('配置复制成功')
        loadConfigs() // 重新加载配置列表
      } catch (error) {
        if (error === 'cancel') return
        console.error('复制配置失败:', error)
        const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          '未知错误'
        ElMessage.error('复制配置失败: ' + errorMessage)
      }
    }

    // 判断是否为默认配置（主要基于描述字段）
    const isDefaultConfig = (config) => {
      return config.description && config.description.includes('系统为新用户自动创建')
    }

    // 判断配置是否属于当前用户
    const isOwnConfig = (config) => {
      try {
        // 优先使用后端返回的 is_owned 字段（更准确）
        if (config.isOwned !== undefined) {
          return config.isOwned === 1
        }

        // 降级方案：使用 createdBy 字段
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}')
        if (config.createdBy !== null && config.createdBy !== undefined) {
          return config.createdBy === userInfo.id
        }

        // 如果 createdBy 为空，假设是用户自己的配置（兼容旧数据）
        // 因为共享配置一定会有 createdBy
        return true
      } catch (error) {
        console.error('解析用户信息失败:', error)
        return false
      }
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return '-'
        return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN', { hour12: false })
      } catch (error) {
        console.error('日期格式化失败:', error)
        return '-'
      }
    }

    // 返回用户列表
    const handleBackUserList = () => {
      router.push('/dashboard/users')
    }
    onMounted(async () => {
      await loadConfigs()
    })

    // 监听路由变化，当userId参数变化时重新加载配置
    watch(() => route.query.userId, () => {
      loadConfigs()
    })

    return {
      t,
      configLoading,
      configList,
      currentUserGame,
      isConfigAdmin,
      selectedUserStatus,
      targetUserId,
      targetUsername,
      isViewMode,
      loadConfigs,
      handleUserStatusChange,
      showAddConfigDialog,
      editConfig,
      deleteConfigConfirm,
      copyConfigHandler,
      isDefaultConfig,
      isOwnConfig,
      formatDate,
      handleShareChange,
      handleBackUserList
    }
  }
}
</script>

<style scoped>
.page-content {
  padding: 0;
  height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.script-config {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding: 0 4px;
}

.header-info {
  display: flex;
  align-items: center;
}

.config-count-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 筛选器区域样式 */
.header-filters {
  display: flex;
  align-items: center;
  gap: 16px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #1d1d1f;
  white-space: nowrap;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  color: #333;
  min-width: 120px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-select:hover {
  border-color: #409EFF;
}

.filter-select:focus {
  outline: none;
  border-color: #409EFF;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.count-text {
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
}

.count-tip {
  font-size: 12px;
  color: #666;
}

.view-user-info {
  font-size: 14px;
  color: #007aff;
  margin-bottom: 8px;
  padding: 8px 12px;
  background: rgba(0, 122, 255, 0.1);
  border: 1px solid rgba(0, 122, 255, 0.2);
  border-radius: 6px;
  display: inline-block;
  cursor: pointer;
}

.view-user-info strong {
  color: #007aff;
  font-weight: 600;
}

/* 自定义主要操作按钮样式 */
.custom-upload-btn {
  background: #409EFF;
  color: white;
  border: none;
  border-radius: 6px;
  width: 120px;
  height: 40px;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
}

.custom-upload-btn:hover {
  background: #66b1ff;
  box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3);
  transform: translateY(-1px);
}

.custom-upload-btn:active {
  background: #3a8ee6;
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
}

.custom-upload-btn.disabled {
  background: #c0c4cc;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.custom-upload-btn.disabled:hover {
  background: #c0c4cc;
  box-shadow: none;
  transform: none;
}

.custom-upload-btn i {
  font-size: 14px;
  margin-right: 6px;
}

.custom-upload-btn span {
  font-size: 14px;
}

/* 配置列表容器 */
.config-list-container {
  flex: 1;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.loading-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-spinner p {
  margin: 0;
  color: #86868b;
  font-size: 16px;
}

/* 表格样式 */
.table-container {
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.table-header {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 2fr 0.8fr 0.8fr 1fr 1.2fr;
  background: #f8f9fa;
  padding: 16px 20px;
  font-weight: 600;
  color: #1d1d1f;
  font-size: 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  gap: 16px;
  min-width: 1000px; /* 设置最小宽度以确保可读性 */
}

/* 普通用户表格布局(没有共享状态列) */
.table-header:not(:has(.shared-col)) {
  grid-template-columns: 1.5fr 1fr 2.5fr 0.8fr 0.8fr 1.5fr;
  min-width: 900px;
}

/* 表头单元格居中 */
.header-cell {
  text-align: center;
  justify-content: center;
}

.table-body {
  flex: 1;
  overflow: auto;
}

.table-row {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr 2fr 0.8fr 0.8fr 1fr 1.2fr;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  align-items: center;
  transition: all 0.2s ease;
  gap: 16px;
  min-width: 1000px; /* 与表头保持一致 */
}

/* 普通用户行布局(没有共享状态列) */
.table-row:not(:has(.shared-col)) {
  grid-template-columns: 1.5fr 1fr 2.5fr 0.8fr 0.8fr 1.5fr;
  min-width: 900px;
}

.table-row:hover {
  background: rgba(0, 0, 0, 0.02);
}

.header-cell, .cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  text-align: center;
}

.cell {
  color: #1d1d1f;
}

.config-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.action-btn {
  background: none;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
}

.action-btn.copy {
  color: #34c759;
  border-color: rgba(52, 199, 89, 0.2);
}

.action-btn.copy:hover {
  background: rgba(52, 199, 89, 0.1);
}

.action-btn.edit {
  color: #007aff;
  border-color: rgba(0, 122, 255, 0.2);
}

.action-btn.edit:hover {
  background: rgba(0, 122, 255, 0.1);
}

.action-btn.delete {
  color: #ff3b30;
  border-color: rgba(255, 59, 48, 0.2);
}

.action-btn.delete:hover:not(:disabled) {
  background: rgba(255, 59, 48, 0.1);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.default-config-tag {
  background: #f0f9ff;
  color: #0369a1;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: default;
}

.shared-config-tag {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: default;
}

/* 空状态样式 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #86868b;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-text {
  font-size: 16px;
  font-weight: 500;
  color: #1d1d1f;
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 14px;
  color: #86868b;
}

/* 平板设备适配 */
@media (max-width: 1024px) and (min-width: 769px) {
  .table-header {
    grid-template-columns: 1fr 0.8fr 1.8fr 0.9fr 1fr;
    min-width: auto;
    padding: 12px 16px;
    gap: 12px;
  }

  .table-header:not(:has(.shared-col)) {
    grid-template-columns: 1.3fr 1fr 2fr 1.2fr;
    min-width: auto;
  }

  .table-row {
    grid-template-columns: 1fr 0.8fr 1.8fr 0.9fr 1fr;
    min-width: auto;
    padding: 12px 16px;
    gap: 12px;
  }

  .table-row:not(:has(.shared-col)) {
    grid-template-columns: 1.3fr 1fr 2fr 1.2fr;
    min-width: auto;
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
    align-items: stretch;
  }

  .header-filters {
    justify-content: center;
    gap: 12px;
  }

  .filter-group {
    gap: 6px;
  }

  .filter-select {
    min-width: 100px;
    padding: 6px 10px;
    font-size: 13px;
  }

  .filter-label {
    font-size: 13px;
  }

  .header-info {
    width: 100%;
  }

  .header-actions {
    width: 100%;
  }

  .custom-upload-btn {
    width: 100%;
    height: 44px;
    font-size: 16px;
  }

  .count-text {
    font-size: 18px;
  }

  .count-tip {
    font-size: 13px;
  }

  /* 隐藏PC端表格 */
  .table-header {
    display: none;
  }

  .table-body {
    padding: 0;
    overflow-x: auto; /* 确保移动端横向滚动可用 */
  }

  /* 手机端卡片式布局 */
  .table-row {
    display: flex;
    flex-direction: column;
    padding: 16px;
    margin-bottom: 12px;
    background: #fff;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    gap: 12px;
    min-width: 320px; /* 设置最小宽度确保内容可读 */
    width: 100%;
  }

  .table-row:hover {
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .cell {
    white-space: normal;
    word-break: break-word;
  }

  /* 卡片标题 */
  .cell.name-col {
    font-size: 16px;
    font-weight: 600;
    color: #1d1d1f;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  }

  /* 游戏标签 */
  .cell.game-col::before {
    content: '游戏：';
    font-weight: 600;
    color: #666;
    margin-right: 4px;
  }

  .cell.game-col {
    font-size: 14px;
  }

  /* 描述 */
  .cell.description-col::before {
    content: '描述：';
    font-weight: 600;
    color: #666;
    margin-right: 4px;
  }

  
  .cell.description-col {
    font-size: 14px;
    color: #666;
  }

  /* 关联的设备数 */
  .cell.device-count-col::before {
    content: '关联的设备数：';
    font-weight: 600;
    color: #666;
    margin-right: 4px;
  }

  .cell.device-count-col {
    font-size: 14px;
    color: #1d1d1f;
  }

  /* 时间 */
  .cell.time-col::before {
    content: '创建时间：';
    font-weight: 600;
    color: #666;
    margin-right: 4px;
  }

  .cell.time-col {
    font-size: 13px;
    color: #999;
  }

  /* 操作按钮 */
  .cell.actions-col {
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }

  .config-actions {
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
  }

  .action-btn {
    flex: 1;
    min-width: 100px;
    padding: 12px 18px;
    font-size: 15px;
    font-weight: 600;
    text-align: center;
    justify-content: center;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    border: none;
    border-radius: 8px;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  /* 手机端复制按钮 - 绿色 */
  .action-btn.copy {
    background: linear-gradient(135deg, #34c759 0%, #30d158 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(52, 199, 89, 0.3);
  }

  .action-btn.copy:active {
    transform: scale(0.97);
    box-shadow: 0 1px 4px rgba(52, 199, 89, 0.3);
  }

  /* 手机端编辑按钮 - 蓝色 */
  .action-btn.edit {
    background: linear-gradient(135deg, #007aff 0%, #0a84ff 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
  }

  .action-btn.edit:active {
    transform: scale(0.97);
    box-shadow: 0 1px 4px rgba(0, 122, 255, 0.3);
  }

  /* 手机端删除按钮 - 红色 */
  .action-btn.delete {
    background: linear-gradient(135deg, #ff3b30 0%, #ff453a 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(255, 59, 48, 0.3);
  }

  .action-btn.delete:active {
    transform: scale(0.97);
    box-shadow: 0 1px 4px rgba(255, 59, 48, 0.3);
  }

  /* 手机端默认配置标签 */
  .default-config-tag {
    flex: 1;
    text-align: center;
    padding: 12px 18px;
    font-size: 15px;
    font-weight: 600;
    background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
    color: white;
    border: none;
    box-shadow: 0 2px 8px rgba(14, 165, 233, 0.3);
    white-space: nowrap;
    min-width: 100px;
  }

  /* 空状态 */
  .empty-state {
    padding: 60px 20px;
  }

  .empty-icon {
    font-size: 64px;
  }

  .empty-text {
    font-size: 18px;
  }

  .empty-desc {
    font-size: 15px;
    text-align: center;
  }

  /* 配置列表容器 */
  .config-list-container {
    border-radius: 8px;
  }
}

/* 超小屏幕（小于480px） */
@media (max-width: 480px) {
  .page-content {
    padding: 0;
  }

  .script-config {
    padding: 0;
  }

  .page-header {
    margin-bottom: 16px;
  }

  .table-row {
    padding: 12px;
    margin-bottom: 8px;
    border-radius: 8px;
  }

  .action-btn {
    min-width: 95px;
    padding: 11px 16px;
    font-size: 14px;
  }

  .default-config-tag {
    padding: 11px 16px;
    font-size: 14px;
    min-width: 95px;
  }

  .cell.name-col {
    font-size: 15px;
  }

  .cell.game-col,
  .cell.description-col {
    font-size: 13px;
  }

  .cell.time-col {
    font-size: 12px;
  }
}

</style>